import { PhpArray, array_keys, array_filter, array_values, empty, identical, in_array } from '../../php-compat';
import type { PortEnv } from '../../port-contract';
import { slot, values, type Arr } from './access';
import { catalogId } from './catalog';
import sourceCatalogs from './source-catalogs.json';
export interface CreatureGateDeps {
    layer_entries(row: Arr, name: string): Arr;
    layer_value(row: Arr, name: string): unknown;
    ft_normalize_layer_entries_to_distinct_uuids(entries: Arr): Arr;
    layer_list_entry_uuid(entry: unknown): string;
    array_intersect(left: Arr, right: Arr): Arr;
    v_status_id_of(row: Arr): string;
    creature_statuses(row: Arr, side: string): Arr;
    has_status_effect(row: Arr, id: string): boolean;
}
/** PHP: ft_creature_type_gate_uuid_lists (includes/functions/janus/core/ft_creature_type_gate.php:33) */
export function ftCreatureTypeGateUuidLists(source: Arr, env: PortEnv<CreatureGateDeps>): Arr {
    const collect = (names: string[]): Arr => closure35CollectIds(names, source, env);
    const divisions = ['sub_type', 'lineage', 'ancestry', 'grouping'];
    return { only: collect(['ability_works_only_against_creature_type']), not: collect(['ability_does_not_work_against_creature_type']),
        only_class: collect(divisions.map(name => 'ability_works_only_against_creature_' + name)),
        not_class: collect(divisions.map(name => 'ability_does_not_work_against_creature_' + name)) };
}
/** PHP: {closure@35:12} (includes/functions/janus/core/ft_creature_type_gate.php:35) */
export function closure35CollectIds(names: Arr, source: Arr, env: PortEnv<CreatureGateDeps>): Arr {
    const out = new PhpArray();
    for (const name of values(names)) for (const id of values(env.deps.ft_normalize_layer_entries_to_distinct_uuids(env.deps.layer_entries(source, name as string))))
        if (id !== '') out.set(id as string, true);
    return array_keys(out);
}
/** PHP: ft_creature_type_gate_is_empty (includes/functions/janus/core/ft_creature_type_gate.php:66) */
export function ftCreatureTypeGateIsEmpty(gate: Arr): boolean {
    return ['only', 'not', 'only_class', 'not_class'].every(key => identical(slot(gate, key) ?? [], []));
}
/** PHP: ft_creature_type_gate_allows (includes/functions/janus/core/ft_creature_type_gate.php:88) */
export function ftCreatureTypeGateAllows(gate: Arr, opposing: Arr | null, env: PortEnv<CreatureGateDeps>): boolean {
    if (ftCreatureTypeGateIsEmpty(gate) || opposing === null || identical(opposing, [])) return true;
    const only = (slot(gate, 'only') ?? []) as Arr, not = (slot(gate, 'not') ?? []) as Arr;
    if (!identical(only, []) || !identical(not, [])) {
        const types = env.deps.ft_normalize_layer_entries_to_distinct_uuids(env.deps.layer_entries(opposing, 'creature_type'));
        if (!identical(only, []) && identical(env.deps.array_intersect(types, only), [])) return false;
        if (!identical(not, []) && !identical(env.deps.array_intersect(types, not), [])) return false;
    }
    const onlyClass = (slot(gate, 'only_class') ?? []) as Arr, notClass = (slot(gate, 'not_class') ?? []) as Arr;
    if (!identical(onlyClass, []) || !identical(notClass, [])) {
        const classes = env.deps.ft_normalize_layer_entries_to_distinct_uuids(env.deps.layer_entries(opposing, 'creature_sub_type'));
        if (!identical(onlyClass, []) && identical(env.deps.array_intersect(classes, onlyClass), [])) return false;
        if (!identical(notClass, []) && !identical(env.deps.array_intersect(classes, notClass), [])) return false;
    }
    return true;
}
/** PHP: ft_target_status_gate_uuid_lists (includes/functions/janus/core/ft_creature_type_gate.php:144) */
export function ftTargetStatusGateUuidLists(source: Arr, env: PortEnv<CreatureGateDeps>): Arr | null {
    const active = array_values(array_filter(env.deps.ft_normalize_layer_entries_to_distinct_uuids(env.deps.layer_entries(source, 'if_target_has_status_effect_active')), closure148ActiveId));
    const inactive = array_values(array_filter(env.deps.ft_normalize_layer_entries_to_distinct_uuids(env.deps.layer_entries(source, 'if_target_has_status_effect_not_active')), closure149InactiveId));
    return active.size === 0 && inactive.size === 0 ? null : { active, not_active: inactive };
}
/** PHP: {closure@148:490} (includes/functions/janus/core/ft_creature_type_gate.php:148) */
export function closure148ActiveId(id: unknown): boolean { return id !== ''; }
/** PHP: {closure@149:510} (includes/functions/janus/core/ft_creature_type_gate.php:149) */
export function closure149InactiveId(id: unknown): boolean { return id !== ''; }
/** PHP: ft_target_status_gate_creature_has_status (includes/functions/janus/core/ft_creature_type_gate.php:157) */
export function ftTargetStatusGateCreatureHasStatus(creature: Arr, id: string, env: PortEnv<CreatureGateDeps>): boolean {
    const key='ft_target_status_gate_creature_has_status::$v_status_ids';
    if (env.functionStatics[key] == null) env.functionStatics[key]=sourceCatalogs.VitalityStatus.map(row=>row.item_id);
    const vitality=env.functionStatics[key] as Arr;
    if (in_array(id, vitality, true) && env.deps.v_status_id_of(creature) === id) return true;
    return !empty(env.deps.creature_statuses(creature, 'target')) && env.deps.has_status_effect(creature, id);
}
